import { useEffect, useMemo, useState } from "react";
import { ArrowClockwise, GraduationCap, Warning } from "@phosphor-icons/react";
import {
  CartesianGrid, Line, LineChart, ReferenceArea, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { api, fmtError } from "../lib/api";

const number = (value, suffix = "") => value === null || value === undefined
  ? "—"
  : `${Number(value).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}${suffix}`;

const BAND_COLORS = {
  non_proficient: "#dc2626",
  high_risk: "#ea580c",
  moderate_risk: "#f59e0b",
  low_risk: "#eab308",
  proficient: "#15803d",
};

const riskTone = (risk) => risk === "non_proficient"
  ? "border-red-600 bg-red-600/10 text-red-700"
  : risk === "high_risk"
    ? "border-orange-600 bg-orange-600/10 text-orange-800"
    : risk === "moderate_risk"
      ? "border-amber-500 bg-amber-500/10 text-amber-800"
      : risk === "low_risk"
        ? "border-yellow-500 bg-yellow-500/10 text-yellow-800"
        : "border-green-700 bg-green-700/10 text-green-800";

function HistoryDot({ cx, cy, payload }) {
  const color = BAND_COLORS[payload?.classification?.code] || "hsl(var(--primary))";
  return <circle cx={cx} cy={cy} r={6} fill={color} stroke="white" strokeWidth={2} />;
}

function HistoryTooltip({ active, payload }) {
  const item = payload?.[0]?.payload;
  if (!active || !item) return null;
  return (
    <div className="border border-border bg-background p-3 shadow-xl text-xs min-w-60">
      <div className="font-mono-data font-bold">{item.exam_name || "Prova"}</div>
      <div className="font-display text-2xl font-black mt-2" style={{ color: BAND_COLORS[item.classification?.code] }}>{number(item.score, "%")}</div>
      <div className="font-semibold mt-1">{item.classification?.label}</div>
      {item.additional_hits_to_proficient > 0 && <div className="text-destructive mt-2">Faltaram {item.additional_hits_to_proficient} acerto(s) para alcançar 60%.</div>}
      {!item.additional_hits_to_proficient && <div className="text-[hsl(var(--success))] mt-2">Proficiência de 60% alcançada.</div>}
    </div>
  );
}

function ProjectionCards({ projection }) {
  if (!projection) return null;
  const evidence = projection.empirical_estimate?.evidence || {};
  const difficulty = evidence.observed_difficulty || {};
  return (
    <div className="grid grid-cols-2 xl:grid-cols-5 gap-px bg-border border border-border mt-5">
      {[
        ["estimativa central", number(projection.projected_score), "percentual esperado · não oficial"],
        ["cenário pessimista", number(projection.pessimistic_score), `margem −${number(projection.margin_points)}`],
        ["nota atual", number(projection.current_score), "última prova realizada"],
        ["tendência", projection.trend?.label || "—", `${number(projection.trend?.slope_points_per_exam, " p.p.")} por prova`],
        ["base histórica", projection.attempt_count || 0, `${projection.confidence || "baixa"} confiança · ${evidence.valid_items_answered || 0} itens`],
      ].map(([label, value, hint]) => (
        <div key={label} className="bg-card p-4">
          <div className="label-eyebrow">{label}</div>
          <div className="font-display text-2xl xl:text-3xl font-black mt-1 leading-tight">{value}</div>
          <div className="text-[11px] text-muted-foreground mt-2 font-mono-data">{hint}</div>
        </div>
      ))}
      {difficulty.available && (
        <div className="col-span-2 xl:col-span-5 bg-card px-4 py-3 border-t border-border text-xs text-muted-foreground">
          <strong className="text-foreground">Contexto das provas:</strong> {difficulty.label} · média das coortes {number(difficulty.mean_percentage_correct, "%")}. A dificuldade observada contextualiza a leitura e não aumenta nem reduz artificialmente a nota.
        </div>
      )}
    </div>
  );
}

function ProjectionChart({ projection }) {
  const history = projection?.history || [];
  if (!history.length) return null;
  return (
    <div className="h-[310px] mt-5 border border-border p-3">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={history} margin={{ top: 12, right: 18, left: -10, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} />
          <YAxis domain={[0, 100]} ticks={[0, 20, 40, 60, 80, 100]} tick={{ fontSize: 10 }} unit="%" />
          <ReferenceArea y1={0} y2={55} fill="#dc2626" fillOpacity={0.06} />
          <ReferenceArea y1={55} y2={57.5} fill="#f59e0b" fillOpacity={0.10} />
          <ReferenceArea y1={57.5} y2={60} fill="#eab308" fillOpacity={0.12} />
          <ReferenceArea y1={60} y2={100} fill="#15803d" fillOpacity={0.05} />
          <Tooltip content={<HistoryTooltip />} />
          <ReferenceLine y={55} stroke="#dc2626" strokeDasharray="3 5" />
          <ReferenceLine y={57.5} stroke="#f59e0b" strokeDasharray="3 5" />
          <ReferenceLine y={60} stroke="#15803d" strokeWidth={2} label={{ value: "≥60 proficiente", fontSize: 10, fill: "#15803d" }} />
          <ReferenceLine y={projection.projected_score} stroke="hsl(var(--primary))" strokeDasharray="3 3" />
          <Line type="monotone" dataKey="score" stroke="hsl(var(--foreground))" strokeWidth={2} dot={<HistoryDot />} activeDot={{ r: 7 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function MethodologyNotice({ methodology }) {
  return (
    <div className="border border-primary/40 bg-primary/5 p-4 mt-5 text-xs text-muted-foreground leading-relaxed" data-testid="empirical-proficiency-methodology">
      <strong className="text-foreground">{methodology?.title || "Projeção empírica de proficiência SAKI/Xm — estimativa não oficial"}.</strong> {methodology?.description}
      {!!methodology?.evidence_components?.length && <div className="mt-2">Evidências consideradas: {methodology.evidence_components.join("; ")}.</div>}
      <div className="mt-1">{methodology?.difficulty_notice}</div>
      <div className="mt-1">{methodology?.cutoff_notice}</div>
    </div>
  );
}

function StudentProfile({ data }) {
  if (data?.eligible === false) return null;
  const projection = data?.projection;
  if (!projection) {
    return <div className="border border-dashed border-border p-12 text-center text-sm text-muted-foreground mt-5">Ainda não há prova válida suficiente para projetar o desempenho.</div>;
  }
  return (
    <>
      <div className={`border-2 p-4 mt-5 flex items-start gap-3 ${riskTone(projection.risk_level)}`} data-testid="student-enamed-risk">
        <Warning size={22} weight="fill" className="shrink-0" />
        <div>
          <div className="font-mono-data font-bold uppercase tracking-wider text-xs">{projection.classification?.label}</div>
          <div className="text-sm mt-1">{projection.risk_message}</div>
          {projection.history?.at(-1)?.additional_hits_to_proficient > 0 && (
            <div className="font-display text-xl font-black mt-2">
              Na última prova faltaram {projection.history.at(-1).additional_hits_to_proficient} acerto(s) para alcançar 60%.
            </div>
          )}
        </div>
      </div>
      <ProjectionCards projection={projection} />
      <ProjectionChart projection={projection} />

      <div className="grid xl:grid-cols-2 gap-5 mt-5">
        <section className="border border-border bg-card">
          <div className="p-4 border-b border-border">
            <div className="label-eyebrow">sua posição</div>
            <h3 className="font-display text-2xl font-black mt-1">Comparação com suas coortes</h3>
          </div>
          <div className="divide-y divide-border">
            {(data.positions || []).map((item, index) => (
              <div key={`${item.scope}-${item.label}-${index}`} className="p-4 grid grid-cols-[1fr_auto] gap-4 items-center">
                <div>
                  <div className="font-semibold">{item.label}</div>
                  <div className="text-xs text-muted-foreground mt-1">média {number(item.cohort_average)} · diferença {item.difference_from_average > 0 ? "+" : ""}{number(item.difference_from_average)} p.p.</div>
                </div>
                <div className="text-right">
                  <div className="font-display text-2xl font-black">{item.rank}º <span className="text-sm text-muted-foreground">/{item.total}</span></div>
                  <div className="text-xs font-mono-data">percentil {number(item.percentile)}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="border border-border bg-card">
          <div className="p-4 border-b border-border">
            <div className="label-eyebrow">áreas do conhecimento</div>
            <h3 className="font-display text-2xl font-black mt-1">Sua posição por área</h3>
          </div>
          <div className="divide-y divide-border max-h-[430px] overflow-auto">
            {(data.area_comparison || []).map((item) => (
              <div key={item.name} className="p-4">
                <div className="flex justify-between gap-4">
                  <div className="font-semibold">{item.name}</div>
                  <div className="font-mono-data font-bold">{item.rank}º/{item.total}</div>
                </div>
                <div className="h-2 bg-muted mt-3 overflow-hidden"><div className="h-full bg-primary" style={{ width: `${item.score}%` }} /></div>
                <div className="flex justify-between text-xs text-muted-foreground mt-2"><span>você {number(item.score, "%")}</span><span>grupo {number(item.cohort_average, "%")}</span></div>
              </div>
            ))}
            {!data.area_comparison?.length && <div className="p-8 text-center text-sm text-muted-foreground">As questões realizadas ainda não possuem áreas cadastradas.</div>}
          </div>
        </section>
      </div>

      {data.interinstitutional && (
        <section className="border border-border bg-card mt-5">
          <div className="p-4 border-b border-border"><div className="label-eyebrow">prova interinstitucional</div><h3 className="font-display text-2xl font-black mt-1">Comparação entre escolas</h3></div>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-px bg-border">
            {(data.school_comparison || []).map((item) => <div key={item.id} className="bg-card p-4"><div className="font-semibold">{item.name}</div><div className="font-display text-3xl font-black mt-2">{number(item.average_score)}</div><div className="text-xs text-muted-foreground mt-1">{item.students} aluno(s) · {item.at_risk} em atenção</div></div>)}
          </div>
        </section>
      )}
      <MethodologyNotice methodology={data.methodology} />
    </>
  );
}

function ComparisonGrid({ comparisons }) {
  const groups = [
    ["Turmas", comparisons?.classes || []],
    ["Etapas", comparisons?.stages || []],
    ["Escolas", comparisons?.schools || []],
  ];
  return (
    <div className="grid xl:grid-cols-3 gap-5 mt-5">
      {groups.map(([title, rows]) => (
        <section key={title} className="border border-border bg-card">
          <div className="p-4 border-b border-border"><div className="label-eyebrow">comparação</div><h3 className="font-display text-xl font-black mt-1">{title}</h3></div>
          <div className="divide-y divide-border max-h-80 overflow-auto">
            {rows.map((row) => <div key={row.id} className="p-3 grid grid-cols-[1fr_auto] gap-3"><div><div className="font-semibold text-sm">{row.name}</div><div className="text-xs text-muted-foreground">{row.students} aluno(s) · {row.at_risk} em atenção</div></div><div className="font-display text-xl font-black">{number(row.average_score)}</div></div>)}
            {!rows.length && <div className="p-6 text-center text-xs text-muted-foreground">Sem dados</div>}
          </div>
        </section>
      ))}
    </div>
  );
}

function StaffProfile({ data }) {
  const summary = data?.summary || {};
  const ranking = data?.ranking || [];
  const [areaName, setAreaName] = useState("");
  const selectedArea = useMemo(() => (data?.area_rankings || []).find((item) => item.name === areaName), [data?.area_rankings, areaName]);
  return (
    <>
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-px bg-border border border-border mt-5">
        {[
          ["alunos avaliados", summary.students || 0, "com provas válidas"],
          ["projeção da média", number(summary.projected_average), "percentual de acertos"],
          ["proficientes", summary.proficient_total || 0, "projeção igual ou acima de 60%"],
          ["chance elevada de conversão", summary.high_conversion_chance || 0, "acima de 55% e em tendência de alta"],
        ].map(([label, value, hint]) => <div key={label} className="bg-card p-4"><div className="label-eyebrow">{label}</div><div className="font-display text-3xl font-black mt-1">{value}</div><div className="text-xs text-muted-foreground mt-2">{hint}</div></div>)}
      </div>

      <section className="border border-border bg-card mt-5" data-testid="enamed-proficiency-bands">
        <div className="p-4 border-b border-border"><div className="label-eyebrow">distribuição da projeção</div><h3 className="font-display text-2xl font-black mt-1">Proficiência e riscos de não proficiência</h3></div>
        <div className="grid md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-border">
          {(summary.proficiency_bands || []).map((band) => <div key={band.code} className="p-4 border-t-4" style={{ borderColor: BAND_COLORS[band.code] }}><div className="text-xs font-bold uppercase tracking-wider" style={{ color: BAND_COLORS[band.code] }}>{band.label}</div><div className="font-display text-3xl font-black mt-2">{band.count}</div><div className="text-xs text-muted-foreground mt-1">{number(band.percentage)}% dos avaliados</div></div>)}
        </div>
      </section>

      <section className="border border-border bg-card mt-5 overflow-x-auto" data-testid="enamed-staff-ranking">
        <div className="p-4 border-b border-border"><div className="label-eyebrow">ranking ENAMED pedagógico</div><h3 className="font-display text-2xl font-black mt-1">Alunos por projeção percentual</h3></div>
        <div className="min-w-[980px] grid grid-cols-[70px_1fr_120px_130px_130px_150px_100px] gap-3 px-4 py-3 bg-muted/30 label-eyebrow"><div>#</div><div>Aluno</div><div>Projeção</div><div>Pessimista</div><div>Atual</div><div>Tendência</div><div>Provas</div></div>
        {ranking.map((row) => { const bandCode = row.proficiency_band?.code || row.risk_level; return <div key={row.student_id} className="min-w-[980px] grid grid-cols-[70px_1fr_120px_130px_130px_150px_100px] gap-3 px-4 py-3 border-t-2 items-center" style={{ borderColor: BAND_COLORS[bandCode] }}><div className="font-display text-xl font-black">{row.rank}º</div><div><div className="font-semibold">{row.student_name}</div><div className="text-[10px] font-mono-data font-bold uppercase" style={{ color: BAND_COLORS[bandCode] }}>{row.proficiency_band?.label}</div>{row.high_conversion_chance && <div className="inline-block mt-1 bg-[hsl(var(--success))] text-white px-2 py-1 text-[9px] font-bold uppercase tracking-wider">chance elevada de conversão</div>}</div><div className="font-mono-data font-black" style={{ color: BAND_COLORS[bandCode] }}>{number(row.projected_score)}</div><div className="font-mono-data font-bold">{number(row.pessimistic_score)}</div><div className="font-mono-data">{number(row.projection?.current_score)}</div><div className="text-xs">{row.projection?.trend?.label}</div><div className="font-mono-data">{row.projection?.attempt_count}</div></div>; })}
        {!ranking.length && <div className="p-12 text-center text-sm text-muted-foreground">Nenhum aluno identificado com prova válida.</div>}
      </section>

      <ComparisonGrid comparisons={data?.comparisons} />

      <section className="border border-border bg-card mt-5 p-4">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div><div className="label-eyebrow">ranking por área</div><h3 className="font-display text-2xl font-black mt-1">Desempenho comparado por área do conhecimento</h3></div>
          <select value={areaName} onChange={(event) => setAreaName(event.target.value)} className="border border-border bg-background px-3 py-2 text-sm min-w-64"><option value="">Selecione uma área</option>{(data?.area_rankings || []).map((item) => <option key={item.name} value={item.name}>{item.name} · média {number(item.average_score, "%")}</option>)}</select>
        </div>
        {selectedArea && <div className="border border-border mt-4"><div className="grid grid-cols-[70px_1fr_120px] gap-3 bg-muted/30 p-3 label-eyebrow"><div>#</div><div>Aluno</div><div>Acertos</div></div>{selectedArea.ranking.map((row) => <div key={row.student_id} className="grid grid-cols-[70px_1fr_120px] gap-3 border-t border-border p-3"><div className="font-display font-black">{row.rank}º</div><div>{row.student_name}</div><div className="font-mono-data font-bold">{number(row.score, "%")}</div></div>)}</div>}
      </section>
      <MethodologyNotice methodology={data?.methodology} />
    </>
  );
}

export default function EnamedProfilePanel({ examId = "", classId = "", studentId = "", embedded = false }) {
  const [data, setData] = useState(null);
  const [stamps, setStamps] = useState([]);
  const [stampId, setStampId] = useState("");
  const [stampsLoaded, setStampsLoaded] = useState(Boolean(studentId));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  useEffect(() => {
    if (studentId) return;
    let alive = true;
    api.get("/student-stamps", { params: { profile_type: "enamed" } }).then((response) => {
      if (!alive) return;
      const rows = response.data || [];
      setStamps(rows);
      setStampId((current) => current && rows.some((item) => item.id === current) ? current : (rows[0]?.id || ""));
    }).catch((cause) => alive && setError(fmtError(cause))).finally(() => alive && setStampsLoaded(true));
    return () => { alive = false; };
  }, [studentId]);
  useEffect(() => {
    if (!studentId && !stampsLoaded) return undefined;
    let alive = true;
    setLoading(true);
    setError("");
    const request = studentId
      ? api.get(`/students/${studentId}/enamed-profile`)
      : api.get("/analytics/enamed-profile", { params: { ...(stampId ? { stamp_id: stampId } : {}), ...(examId ? { exam_id: examId } : {}), ...(classId ? { class_id: classId } : {}) } });
    request.then((response) => alive && setData(response.data)).catch((cause) => alive && setError(fmtError(cause))).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [examId, classId, studentId, stampId, stampsLoaded]);

  if (studentId && !loading && data?.eligible === false) return null;

  return (
    <section className={`${embedded ? "" : "border border-border bg-card p-5 md:p-6 mb-6"}`} data-testid={studentId ? "student-enamed-profile" : "staff-enamed-profile"}>
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
        <div className="flex items-start gap-3">
        <GraduationCap size={28} weight="duotone" className="text-primary shrink-0 mt-1" />
        <div>
          <div className="label-eyebrow">perfil ENAMED · estimativa empírica por acertos</div>
          <h2 className="font-display text-3xl font-black tracking-tight mt-1">Projeção empírica de proficiência e posição comparativa</h2>
          {data?.stamp?.name && <div className="inline-block border px-3 py-1 mt-3 text-[10px] font-bold uppercase tracking-wider" style={{ borderColor: data.stamp.color, color: data.stamp.color }}>{data.stamp.name}</div>}
        </div>
        </div>
        {!studentId && stampsLoaded && <label className="lg:min-w-64"><span className="label-eyebrow block mb-1">estampa ENAMED</span><select value={stampId} onChange={(event) => setStampId(event.target.value)} className="w-full border border-border bg-background px-3 py-2.5 text-sm"><option value="">Nenhuma estampa criada</option>{stamps.map((stamp) => <option key={stamp.id} value={stamp.id}>{stamp.name} · {stamp.student_count} aluno(s)</option>)}</select></label>}
      </div>
      {loading && <div className="border border-border p-10 mt-5 text-center label-eyebrow"><ArrowClockwise size={18} className="animate-spin inline mr-2" /> calculando projeção…</div>}
      {!loading && error && <div className="border border-destructive bg-destructive/5 text-destructive p-4 mt-5 text-sm">{error}</div>}
      {!loading && !error && (studentId ? <StudentProfile data={data} /> : <StaffProfile data={data} />)}
    </section>
  );
}
